import * as React from "react";
import { Box, Typography, BoxProps, CardProps, colors, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import GlassCard from "./GlassCard";

export interface CardBaseProps extends Omit<BoxProps, 'component'> {
    title?: string;
    subtitle?: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
    headerActions?: React.ReactNode;
    elevation?: number;
    useGlassCard?: boolean;
    gradientColor?: string;
    glassEffect?: "clear" | "regular" | "none";
    nested?: boolean; // For nested cards that shouldn't blur parent
}

const StyledCard = styled(Box)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    return {
        // pure background (no opacity, no blur)
        background: isDark ? theme.palette.background.default : theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius * 1.25,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            borderRadius: theme.shape.borderRadius * 1,
        },
        // Responsive for mobile landscape
        "@media (orientation: landscape) and (max-width: 900px)": {
            borderRadius: theme.shape.borderRadius * 0.75,
        },
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[4],
        },
    };
});

interface CardSectionProps {
    nested?: boolean;
}

const CardHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.75 : 2.5, nested ? 1.5 : 2),
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    borderTop: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(nested ? 1.5 : 2),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.5 : 2, nested ? 1.5 : 2),
        gap: theme.spacing(nested ? 1.25 : 1.75),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 1 : 1.5, nested ? 0.5 : 0.75),
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(nested ? 0.75 : 1),
        borderTop: "none",
    },
    // Responsive for mobile landscape
    "@media (orientation: landscape) and (max-width: 900px)": {
        padding: theme.spacing(nested ? 0.75 : 1, nested ? 0.5 : 0.75),
        gap: theme.spacing(nested ? 0.5 : 0.75),
    },
}));

const CardContent = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.5 : 2, nested ? 1.5 : 2),
    position: "relative",
    zIndex: 1,
    borderTop: "none",
    borderBottom: "none",
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.25 : 1.75, nested ? 1.5 : 2),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 0.75 : 1.25, nested ? 0.5 : 0.75),
        borderTop: "none",
        borderBottom: "none",
    },
    // Responsive for mobile landscape
    "@media (orientation: landscape) and (max-width: 900px)": {
        padding: theme.spacing(nested ? 0.5 : 0.75, nested ? 0.5 : 0.75),
    },
}));

const CardActions = styled(Box, {
    shouldForwardProp: (prop) => prop !== "nested",
})<CardSectionProps>(({ theme, nested }) => ({
    padding: theme.spacing(nested ? 1.75 : 2.5, nested ? 1.5 : 2),
    borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(nested ? 0.75 : 1),
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(nested ? 1.25 : 1.75, nested ? 1.75 : 2.5),
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(nested ? 0.75 : 0.75, nested ? 0.5 : 0.75),
        flexDirection: "column",
        alignItems: "stretch",
        gap: theme.spacing(nested ? 0.75 : 1),
    },
    // Responsive for mobile landscape
    "@media (orientation: landscape) and (max-width: 900px)": {
        padding: theme.spacing(nested ? 0.5 : 0.75, nested ? 0.5 : 0.75),
        gap: theme.spacing(nested ? 0.5 : 0.75),
    },
}));

const CardBase = React.forwardRef<HTMLDivElement, CardBaseProps>(({
    title,
    subtitle,
    children,
    actions,
    headerActions,
    elevation = 2,
    useGlassCard = false,
    gradientColor,
    glassEffect,
    nested = false,
    sx,
    ...props
}, ref) => {
    const theme = useTheme();
    const content = (
        <>
            {(title || subtitle || headerActions) && (
                <CardHeader
                    className="card-base__header"
                    nested={nested}
                    sx={{
                        [theme.breakpoints.down("sm")]: {
                            flexWrap: "nowrap",
                            alignItems: "flex-start",
                        }
                    }}
                >
                    <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        [theme.breakpoints.down("sm")]: {
                            alignItems: "flex-start",
                            px: nested ? 0.5 : 0.75,
                        },
                        // Responsive for mobile landscape
                        "@media (orientation: landscape) and (max-width: 900px)": {
                            px: nested ? 0.25 : 0.5,
                        },
                    }}>
                        {title && (
                            <Typography
                                variant="h6"
                                component="h2"
                                fontWeight={600}
                                sx={{
                                    color: "#12903C",
                                    fontSize: {
                                        xs: nested ? "0.95rem" : "1.05rem",
                                        sm: nested ? "1.05rem" : "1.15rem",
                                        md: nested ? "1.1rem" : "1.25rem",
                                    },
                                    lineHeight: { xs: 1.2, sm: 1.3 },
                                    [theme.breakpoints.down("sm")]: {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        wordBreak: "break-word",
                                        maxWidth: "100%",
                                    },
                                    // Responsive for mobile landscape
                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                        fontSize: nested ? "0.875rem" : "0.9375rem",
                                        lineHeight: 1.2,
                                        pl: nested ? 0.5 : 0, // Add padding left for nested cards in landscape
                                    },
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.5,
                                    // Responsive for mobile landscape
                                    "@media (orientation: landscape) and (max-width: 900px)": {
                                        fontSize: "0.75rem",
                                        mt: 0.25,
                                    },
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {headerActions && (
                        <Box sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            [theme.breakpoints.down("sm")]: {
                                alignItems: "flex-start",
                                pt: 0.25,
                            }
                        }}>
                            {headerActions}
                        </Box>
                    )}
                </CardHeader>
            )}
            <CardContent className="card-base__content" nested={nested}>{children}</CardContent>
            {actions && <CardActions className="card-base__actions" nested={nested}>{actions}</CardActions>}
        </>
    );

    // Determine background gradient
    const getGradientBackground = () => {
        if (gradientColor) {
            return `linear-gradient(135deg, ${gradientColor}44 0%, rgba(255, 255, 255, 0.3) 100%)`;
        }
        return undefined;
    };

    const backgroundStyle = getGradientBackground();

    if (useGlassCard) {
        return (
            <GlassCard
                ref={ref}
                effect={glassEffect}
                nested={nested}
                sx={{
                    p: 0,
                    ...(backgroundStyle && { background: backgroundStyle }),
                    ...sx,
                }}
            >
                {content}
            </GlassCard>
        );
    }

    return (
        <StyledCard
            ref={ref}
            sx={{
                ...(backgroundStyle && { background: backgroundStyle }),
                ...sx,
            }}
            {...props}
        >
            {content}
        </StyledCard>
    );
});

CardBase.displayName = "CardBase";

export default CardBase;

